/**
 * This can be used to indicate a value should be passed as is (i.e. not
 * transformed). We introduced this symbol to address the issue that some
 * fields are produced through accessors, thereby not appearing in the final
 * result which is produced as a JSON string by Lambda (presumably through
 * JSON.stringify)
 */
export const PASS_THROUGH = Symbol.for('@attain-apps/transformers.PASS_THROUGH')

/**
 * These values can use {@link PASS_THROUGH}, either on their own or in a list
 *
 * Basically, since transformers are often used to transform data types when
 * sending or receiving to/from an external system, there are some cases where a
 * value is equivalent to TypeScript but not when interacting with the external
 * system. For that reason, we do not allow passing through types that might
 * have these issues, primarily objects. See examples below
 *
 * @example
 * type Foo = { foo: 'foo' }
 * class FooWithAccessor implements Foo {
 *   get foo() { return 'foo' }
 * }
 *
 * const f1: Foo = { foo: 'foo' }
 * const f2: Foo = new FooWithAccessor()
 *
 * JSON.stringify(f1) === "{\"foo\":\"foo\"}"
 * JSON.stringify(f2) === "{}"
 *
 * // This would have problems with `f2` above
 * async function process(foo: Foo) {
 *   await client.send(JSON.stringify(foo))
 * }
 */
type PassThroughEligibleElement = Primitive | Nullish | void

/**
 * Allows {@link PASS_THROUGH} for eligible input/output combinations
 */
export type PassThroughIfPossible<TFrom, TTo> = unknown extends TTo
  ? // If `TTo` is unknown, then we allow `PASS_THROUGH` because
    // any `TFrom` should be usable for `TTo`
    typeof PASS_THROUGH
  : NullToUndefined<TFrom> extends NullToUndefined<TTo>
    ? TFrom extends
        | PassThroughEligibleElement
        | readonly PassThroughEligibleElement[]
      ? // If `TFrom` is assignable to `TTo` and `TFrom` is eligible for passing
        // through (@see `PassThroughEligibleElement`), then we allow `PASS_THROUGH`
        typeof PASS_THROUGH
      : never
    : never
